/**
 * Continuous performance grader: playhead advances on a fixed timeline.
 * Auto-misses when the playhead passes a step's miss window.
 */

import type { MusicPiece } from "../music/types.js";
import {
  buildExpectedSteps,
  type ExpectedStep,
  type FollowAlongHit,
  type FollowAlongStats,
  type HitQuality,
} from "./FollowAlongEngine.js";

export class TimedPlayheadGrader {
  private steps: ExpectedStep[] = [];
  private index = 0;
  private pending = new Set<number>();
  private hits = 0;
  private wrongs = 0;
  private perfect = 0;
  private early = 0;
  private late = 0;
  private deltas: number[] = [];
  private errorByMidi: Record<string, number> = {};
  private active = false;
  private pieceId: string | null = null;
  private pieceBpm = 90;
  private originAudioTime = 0;
  private wallStartedMs = 0;
  private missWindowMs = 180;
  private perfectWindowMs = 40;
  private hitWindowMs = 90;

  get isActive(): boolean {
    return this.active;
  }

  get pieceIdLoaded(): string | null {
    return this.pieceId;
  }

  load(piece: MusicPiece, originAudioTime: number): void {
    this.steps = buildExpectedSteps(piece);
    this.index = 0;
    this.hits = 0;
    this.wrongs = 0;
    this.perfect = 0;
    this.early = 0;
    this.late = 0;
    this.deltas = [];
    this.errorByMidi = {};
    this.active = true;
    this.pieceId = piece.id;
    this.pieceBpm = piece.bpm || 90;
    this.originAudioTime = originAudioTime;
    this.wallStartedMs = performance.now();
    this.armStep();
  }

  clear(): void {
    this.active = false;
    this.steps = [];
    this.index = 0;
    this.pending.clear();
    this.pieceId = null;
  }

  getExpectedMidiList(): number[] {
    return this.getExpected()?.midis ?? [];
  }

  getExpected(): ExpectedStep | null {
    if (!this.active || this.index >= this.steps.length) return null;
    return this.steps[this.index];
  }

  getPlayheadBeat(audioTime: number): number {
    const beatSec = 60 / Math.max(1, this.pieceBpm);
    return Math.max(0, (audioTime - this.originAudioTime) / beatSec);
  }

  getStats(): FollowAlongStats {
    const expectedCount = this.steps.length;
    const completed = this.active && this.index >= this.steps.length;
    const judged = this.hits + this.wrongs;
    const elapsedSec =
      this.wallStartedMs > 0 ? (performance.now() - this.wallStartedMs) / 1000 : 0;
    const n = this.deltas.length;
    const mean = n ? this.deltas.reduce((a, b) => a + b, 0) / n : 0;
    const variance = n
      ? this.deltas.reduce((a, d) => a + (d - mean) ** 2, 0) / n
      : 0;
    return {
      expectedCount,
      hits: this.hits,
      wrongs: this.wrongs,
      remaining: Math.max(0, expectedCount - this.index),
      accuracyPct: judged ? (this.hits / judged) * 100 : 100,
      completed,
      perfect: this.perfect,
      early: this.early,
      late: this.late,
      meanDeltaMs: mean,
      jitterMs: Math.sqrt(variance),
      velocityHits: 0,
      velocityMisses: 0,
      notesPerMinute: elapsedSec > 0.5 ? (this.hits / elapsedSec) * 60 : this.hits * 60,
      elapsedSec,
      errorByMidi: { ...this.errorByMidi },
    };
  }

  /** Advance auto-misses based on playhead. Call from a tick loop. */
  tick(audioTime: number): boolean {
    if (!this.active || this.index >= this.steps.length) return this.getStats().completed;
    const beatSec = 60 / Math.max(1, this.pieceBpm);
    while (this.index < this.steps.length) {
      const step = this.steps[this.index];
      const expectedTime = this.originAudioTime + step.beat * beatSec;
      const lateByMs = (audioTime - expectedTime) * 1000;
      if (lateByMs <= this.missWindowMs) break;
      // Miss remaining notes in the chord and advance.
      for (const midi of this.pending) {
        this.wrongs += 1;
        const key = String(midi);
        this.errorByMidi[key] = (this.errorByMidi[key] ?? 0) + 1;
      }
      this.pending.clear();
      this.index += 1;
      this.armStep();
    }
    return this.getStats().completed;
  }

  handleNoteOn(midiNote: number, audioTime: number): FollowAlongHit {
    if (!this.active || this.index >= this.steps.length) {
      return {
        quality: "wrong",
        expected: null,
        playedMidi: midiNote,
        stepIndex: this.index,
      };
    }

    // Catch up misses before grading this note.
    this.tick(audioTime);
    if (this.index >= this.steps.length) {
      return {
        quality: "wrong",
        expected: null,
        playedMidi: midiNote,
        stepIndex: this.index,
      };
    }

    const expected = this.steps[this.index];
    if (!this.pending.has(midiNote)) {
      this.wrongs += 1;
      const key = String(midiNote);
      this.errorByMidi[key] = (this.errorByMidi[key] ?? 0) + 1;
      return {
        quality: "wrong",
        expected,
        playedMidi: midiNote,
        stepIndex: this.index,
      };
    }

    this.pending.delete(midiNote);
    const beatSec = 60 / Math.max(1, this.pieceBpm);
    const expectedTime = this.originAudioTime + expected.beat * beatSec;
    const deltaMs = (audioTime - expectedTime) * 1000;
    this.deltas.push(deltaMs);
    const abs = Math.abs(deltaMs);
    let quality: HitQuality = "hit";
    if (abs <= this.perfectWindowMs) {
      quality = "perfect";
      this.perfect += 1;
    } else if (abs <= this.hitWindowMs) {
      quality = "hit";
    } else if (deltaMs < 0) {
      quality = "early";
      this.early += 1;
    } else {
      quality = "late";
      this.late += 1;
    }

    if (this.pending.size === 0) {
      this.hits += 1;
      this.index += 1;
      this.armStep();
      return {
        quality,
        expected,
        playedMidi: midiNote,
        stepIndex: this.index - 1,
        deltaMs,
      };
    }

    return {
      quality,
      expected,
      playedMidi: midiNote,
      stepIndex: this.index,
      deltaMs,
    };
  }

  private armStep(): void {
    this.pending.clear();
    const step = this.steps[this.index];
    if (step) {
      for (const m of step.midis) this.pending.add(m);
    }
  }
}
